<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Seller Product Variant</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Assign a product variant to a seller with pricing and inventory details
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-package-variant</v-icon>
                      New Listing
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Seller Product Variants
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Product Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-package-variant-closed</v-icon>
                    Product Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6" v-if="!isSeller">
                      <v-autocomplete
                        v-model="form.position_id"
                        :items="positionItems"
                        :loading="positionLoading"
                        label="Visibility (Position)"
                        placeholder="All users (no restriction)"
                        outlined
                        clearable
                        :error-messages="errors.position_id"
                        @input="clearError('position_id')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account-eye</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6" v-if="!isSeller">
                      <v-autocomplete
                        v-model="form.seller_id"
                        :items="sellerItems"
                        :search-input.sync="sellerSearch"
                        :loading="sellerLoading"
                        no-filter
                        label="Seller"
                        placeholder="Type to search sellers..."
                        outlined
                        :rules="[(v) => !!v || 'Seller is required']"
                        :error-messages="errors.seller_id"
                        @input="clearError('seller_id')"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                        <template #append-item>
                          <div
                            v-if="sellerHasMore"
                            v-intersect="{ handler: onSellerIntersect, options: { threshold: 0.5 } }"
                            class="text-center py-2"
                          >
                            <v-progress-circular v-if="sellerLoading" indeterminate size="18" color="primary"></v-progress-circular>
                            <span v-else class="caption grey--text">Loading more...</span>
                          </div>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.product_variant_id"
                        :items="productVariantItems"
                        :search-input.sync="variantSearch"
                        :loading="variantLoading"
                        no-filter
                        label="Product Variant"
                        placeholder="Type to search variants..."
                        outlined
                        :rules="[(v) => !!v || 'Product variant is required']"
                        :error-messages="errors.product_variant_id"
                        @input="clearError('product_variant_id')"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-package-variant</v-icon>
                        </template>
                        <template #append-item>
                          <div
                            v-if="variantHasMore"
                            v-intersect="{ handler: onVariantIntersect, options: { threshold: 0.5 } }"
                            class="text-center py-2"
                          >
                            <v-progress-circular v-if="variantLoading" indeterminate size="18" color="primary"></v-progress-circular>
                            <span v-else class="caption grey--text">Loading more...</span>
                          </div>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Pricing Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-currency-inr</v-icon>
                    Pricing Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.maximum_retail_price"
                        label="Maximum Retail Price"
                        placeholder="Enter MRP"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.maximum_retail_price"
                        @input="clearError('maximum_retail_price')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-tag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.seller_discount"
                        label="Seller Discount"
                        placeholder="Enter seller discount amount"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.seller_discount"
                        @input="clearError('seller_discount')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-tag-minus</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.ht_discount"
                        label="HT Discount"
                        placeholder="Enter HT discount amount"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.ht_discount"
                        @input="clearError('ht_discount')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-sale</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.consumer_discount"
                        label="Consumer Discount"
                        placeholder="Enter consumer discount amount"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.consumer_discount"
                        @input="clearError('consumer_discount')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account-cash</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.doctor_discount"
                        label="Doctor Discount"
                        placeholder="Enter doctor discount amount"
                        outlined
                        type="number"
                        step="0.01"
                        min="0"
                        :error-messages="errors.doctor_discount"
                        @input="clearError('doctor_discount')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.stock"
                        label="Stock Quantity"
                        placeholder="Enter stock quantity"
                        outlined
                        type="number"
                        min="0"
                        :error-messages="errors.stock"
                        @input="clearError('stock')"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-package</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Settings Section -->
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors()
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors()
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="isLoading || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Listing
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellerProductVariantCreatePage",
  data() {
    return {
      valid: false,
      form: {
        seller_id: null,
        product_variant_id: null,
        seller_product_id: null,
        position_id: null,
        maximum_retail_price: null,
        seller_discount: null,
        ht_discount: null,
        consumer_discount: null,
        doctor_discount: null,
        stock: 0,
        status: true,
        is_active: true,
      },
      errors: {},
      // Position dropdown
      positionItems: [],
      positionLoading: false,
      // Seller dropdown
      sellerItems: [],
      sellerSearch: '',
      sellerPage: 1,
      sellerTotal: 0,
      sellerLoading: false,
      // Product variant dropdown
      productVariantItems: [],
      variantSearch: '',
      variantPage: 1,
      variantTotal: 0,
      variantLoading: false,
      // Debounce timers
      sellerTimer: null,
      variantTimer: null,
      isLoading: false,
    };
  },
  watch: {
    'form.position_id'() {
      this.clearError('position_id');
    },
    'form.seller_id'() {
      this.clearError('seller_id');
    },
    'form.product_variant_id'() {
      this.clearError('product_variant_id');
    },
    'form.seller_product_id'() {
      this.clearError('seller_product_id');
    },
    'form.maximum_retail_price'() {
      this.clearError('maximum_retail_price');
    },
    'form.seller_discount'() {
      this.clearError('seller_discount');
    },
    'form.ht_discount'() {
      this.clearError('ht_discount');
    },
    'form.consumer_discount'() {
      this.clearError('consumer_discount');
    },
    'form.doctor_discount'() {
      this.clearError('doctor_discount');
    },
    'form.stock'() {
      this.clearError('stock');
    },
    'form.status'() {
      this.clearError('status');
    },
    'form.is_active'() {
      this.clearError('is_active');
    },
    sellerSearch(val) {
      clearTimeout(this.sellerTimer);
      this.sellerTimer = setTimeout(() => this.loadSellers(true, val), 300);
    },
    variantSearch(val) {
      clearTimeout(this.variantTimer);
      this.variantTimer = setTimeout(() => this.loadVariants(true, val), 300);
    },
  },
  computed: {
    isSeller() {
      const pos = this.userPosition && this.userPosition.name ? this.userPosition.name.toUpperCase() : '';
      return pos === 'SELLER';
    },
    sellerHasMore() {
      return this.sellerItems.length < this.sellerTotal;
    },
    variantHasMore() {
      return this.productVariantItems.length < this.variantTotal;
    },
  },

  mounted() {
    this.loadPositions();
    this.loadSellers(true);
    this.loadVariants(true);
    if (this.isSeller && this.$store.state.auth.user) {
      this.form.seller_id = this.$store.state.auth.user.id;
    }
  },

  methods: {
    async loadPositions() {
      this.positionLoading = true;
      try {
        const res = await axios.get('/positions', { params: { rowsPerPage: 100 } });
        const records = res.data.data || [];
        this.positionItems = records.map(p => ({ value: p.id, text: p.name }));
      } catch (e) {
        if (e.response?.status === 401) window.location.href = '/auth/login';
      } finally {
        this.positionLoading = false;
      }
    },

    async loadSellers(reset = false, search = this.sellerSearch) {
      if (this.sellerLoading) return;
      if (reset) { this.sellerPage = 1; this.sellerItems = []; }
      this.sellerLoading = true;
      try {
        const res = await axios.get('/masters', {
          params: {
            context: 'seller_product_variant',
            key: 'users',
            page: this.sellerPage,
            rowsPerPage: 20,
            search_keyword: search || '',
          },
        });
        const payload = res.data.users || {};
        const records = payload.data || [];
        const items = records.map(u => ({
          value: u.id,
          text: `${u.first_name} ${u.last_name} (${u.email})`,
        }));
        this.sellerItems = reset ? items : [...this.sellerItems, ...items];
        this.sellerTotal = payload.count || 0;
        this.sellerPage++;
      } catch (e) {
        if (e.response?.status === 401) window.location.href = '/auth/login';
      } finally {
        this.sellerLoading = false;
      }
    },

    async loadVariants(reset = false, search = this.variantSearch) {
      if (this.variantLoading) return;
      if (reset) { this.variantPage = 1; this.productVariantItems = []; }
      this.variantLoading = true;
      try {
        const res = await axios.get('/masters', {
          params: {
            context: 'seller_product_variant',
            key: 'product_variants',
            page: this.variantPage,
            rowsPerPage: 20,
            search_keyword: search || '',
          },
        });
        const payload = res.data.product_variants || {};
        const records = payload.data || [];
        const items = records.map(v => ({
          value: v.id,
          text: v.sku_id ? `${v.name} (${v.sku_id})` : v.name,
        }));
        this.productVariantItems = reset ? items : [...this.productVariantItems, ...items];
        this.variantTotal = payload.count || 0;
        this.variantPage++;
      } catch (e) {
        if (e.response?.status === 401) window.location.href = '/auth/login';
      } finally {
        this.variantLoading = false;
      }
    },

    onSellerIntersect(entries) {
      if (entries[0].isIntersecting && this.sellerHasMore) this.loadSellers();
    },

    onVariantIntersect(entries) {
      if (entries[0].isIntersecting && this.variantHasMore) this.loadVariants();
    },

    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },


    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.isLoading = true;
        this.errors = {};

        const payload = { ...this.form };
        
        // Convert numeric fields
        if (payload.maximum_retail_price) {
          payload.maximum_retail_price = parseFloat(payload.maximum_retail_price);
        }
        if (payload.seller_discount) {
          payload.seller_discount = parseFloat(payload.seller_discount);
        }
        if (payload.ht_discount) {
          payload.ht_discount = parseFloat(payload.ht_discount);
        }
        if (payload.consumer_discount) {
          payload.consumer_discount = parseFloat(payload.consumer_discount);
        }
        if (payload.doctor_discount) {
          payload.doctor_discount = parseFloat(payload.doctor_discount);
        }
        if (payload.stock !== null && payload.stock !== '') {
          payload.stock = parseInt(payload.stock);
        }

        await axios.post("/seller_product_variants", payload);
        this.isLoading = false;
        this.$router.push("/seller-product-variants");
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error creating seller product variant:", error);
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 0.85rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }

  .form-content {
    padding: 0.5rem !important;
  }

  .section-title {
    font-size: 0.8rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 36px;
  }

  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>